import { useEffect, useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/ui/status-badge';
import { EarningsAPI, EarningsHistoryResponse } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

export default function EarningsHistoryPage() {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<EarningsHistoryResponse | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '' as '' | 'pending' | 'paid' | 'failed' | 'refunded',
    entityType: '' as '' | 'supplier' | 'transporter' | 'admin',
    fromDate: '',
    toDate: '',
    region: '',
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 20 };
      if (filters.status) params.status = filters.status;
      if (filters.entityType) params.entityType = filters.entityType;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.region) params.region = filters.region;
      if (filters.sortBy) params.sortBy = filters.sortBy;
      if (filters.sortOrder) params.sortOrder = filters.sortOrder;
      
      const response = await EarningsAPI.getHistory(params);
      if (response.success) {
        setHistory(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching earnings history:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export earnings history');
  };

  if (loading && !history) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading earnings history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Earnings History</h1>
          <p className="text-muted-foreground">View all earnings transactions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Entity Type</label>
                <select
                  value={filters.entityType}
                  onChange={(e) => setFilters({ ...filters, entityType: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">All</option>
                  <option value="supplier">Supplier</option>
                  <option value="transporter">Transporter</option>
                  <option value="admin">Platform</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Region</label>
                <input
                  type="text"
                  value={filters.region}
                  onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                  placeholder="Filter by region"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sort Order</label>
                <select
                  value={filters.sortOrder}
                  onChange={(e) => setFilters({ ...filters, sortOrder: e.target.value as any })}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={fetchHistory}>Apply Filters</Button>
              <Button
                variant="outline"
                onClick={() => {
                  setFilters({ 
                    status: '', 
                    entityType: '',
                    fromDate: '', 
                    toDate: '',
                    region: '',
                    sortBy: 'createdAt',
                    sortOrder: 'desc'
                  });
                  setPage(1);
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Earnings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
          <CardDescription>
            {history?.pagination.totalItems || 0} total transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-medium">Order Number</th>
                  <th className="text-left p-3 font-medium">Date</th>
                  <th className="text-right p-3 font-medium">Order Total</th>
                  <th className="text-right p-3 font-medium">Supplier</th>
                  <th className="text-right p-3 font-medium">Transporter</th>
                  <th className="text-right p-3 font-medium">Platform</th>
                  <th className="text-center p-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {history?.earnings.map((earning) => (
                  <tr key={earning._id} className="border-b hover:bg-muted/50">
                    <td className="p-3 font-medium">{earning.orderNumber}</td>
                    <td className="p-3 text-sm text-muted-foreground">
                      {new Date(earning.earnedAt).toLocaleString()}
                    </td>
                    <td className="p-3 text-right font-medium">
                      {formatCurrency(earning.totalOrderValue || earning.orderTotal || 0)}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {formatCurrency(earning.supplierPayments)}
                    </td>
                    <td className="p-3 text-right text-sm">
                      {formatCurrency(earning.transporterPayments)}
                    </td>
                    <td className="p-3 text-right font-semibold text-green-600">
                      {formatCurrency(earning.platformEarnings)}
                    </td>
                    <td className="p-3 text-center">
                      <StatusBadge status={earning.paymentStatus} label={earning.paymentStatus || 'N/A'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(!history?.earnings || history.earnings.length === 0) && (
              <div className="text-center py-12 text-muted-foreground">
                No earnings history found
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {history && history.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={!history.pagination.hasPrevPage}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2">
            Page {history.pagination.currentPage} of {history.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={!history.pagination.hasNextPage}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
